/**
 * Progressive Photon address lookup for checkout billing/shipping.
 */

function getConfig() {
	return window.doneCommerce || {};
}

function debounce(fn, wait) {
	let timer;
	return (...args) => {
		window.clearTimeout(timer);
		timer = window.setTimeout(() => fn(...args), wait);
	};
}

function escapeHtml(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function getDeliveryMethod() {
	const form = document.querySelector('[data-dc-checkout]');
	return form?.dataset.deliveryMethod || 'delivery';
}

function isBillingPanel() {
	return Boolean(document.querySelector('[data-dc-checkout]')?.classList.contains('is-address-billing'));
}

function fieldHasValue(id) {
	const el = document.getElementById(id);
	return Boolean(el && String(el.value || '').trim() !== '');
}

function addressLooksFilled(prefix) {
	return (
		fieldHasValue(`${prefix}_address_1`) ||
		(fieldHasValue(`${prefix}_city`) && fieldHasValue(`${prefix}_postcode`))
	);
}

function setFieldValue(id, value) {
	const el = document.getElementById(id);
	if (!el) {
		return;
	}
	el.value = value ?? '';
	el.dispatchEvent(new Event('input', { bubbles: true }));
	el.dispatchEvent(new Event('change', { bubbles: true }));
}

function ensureApartmentLink(address1Row) {
	if (!address1Row || address1Row.parentElement?.querySelector('[data-apartment-toggle]')) {
		return;
	}

	const link = document.createElement('button');
	link.type = 'button';
	link.className = 'dc-apartment-toggle';
	link.setAttribute('data-apartment-toggle', '');
	link.textContent = getConfig().i18n?.addApartment || '+ Add apartment, suite, etc.';
	address1Row.insertAdjacentElement('afterend', link);

	link.addEventListener('click', () => {
		const root = address1Row.closest('.woocommerce-billing-fields, .woocommerce-shipping-fields, .shipping_address');
		root?.classList.add('is-apartment-open');
		const address2 =
			root?.querySelector('#billing_address_2_field, #shipping_address_2_field') ||
			address1Row.parentElement?.querySelector('.dc-checkout-address-2');
		const input = address2?.querySelector('input');
		input?.focus();
	});
}

function ensureAddressLookup(container, prefix) {
	if (!container) {
		return null;
	}

	let wrap = container.querySelector(`[data-address-lookup="${prefix}"]`);
	if (wrap) {
		return wrap;
	}

	const i18n = getConfig().i18n || {};
	wrap = document.createElement('div');
	wrap.className = 'dc-address-lookup form-row';
	wrap.setAttribute('data-address-lookup', prefix);
	wrap.innerHTML = `
		<div class="dc-address-lookup__field">
			<input type="text" class="input-text dc-address-lookup__input" autocomplete="off" aria-autocomplete="list" aria-expanded="false" role="combobox" data-address-lookup-input>
			<label>${escapeHtml(i18n.addressLookup || 'Start typing your address')}</label>
			<span class="dc-address-lookup__spinner" data-address-lookup-spinner hidden aria-hidden="true"></span>
		</div>
		<ul class="dc-address-lookup__results" data-address-lookup-results role="listbox" hidden></ul>
		<button type="button" class="dc-address-lookup__manual" data-address-lookup-manual>
			${escapeHtml(i18n.enterAddressManually || 'Enter address manually')}
		</button>
	`;

	const heading = container.querySelector(':scope > h3, :scope > [data-delivery-title]');
	if (heading?.nextSibling) {
		container.insertBefore(wrap, heading.nextSibling);
	} else {
		container.insertBefore(wrap, container.firstChild);
	}

	return wrap;
}

async function fetchSuggestions(query) {
	const config = getConfig();
	const response = await fetch(
		`${config.restUrl}done-commerce/v1/address-lookup?q=${encodeURIComponent(query)}`,
		{
			credentials: 'same-origin',
			headers: {
				'X-WP-Nonce': config.nonce || '',
			},
		}
	);

	if (!response.ok) {
		throw new Error('lookup failed');
	}

	const data = await response.json();
	return Array.isArray(data.results) ? data.results : [];
}

function fillAddress(prefix, result) {
	setFieldValue(`${prefix}_country`, result.country || 'AU');
	setFieldValue(`${prefix}_address_1`, result.address_1 || '');
	setFieldValue(`${prefix}_address_2`, result.address_2 || '');
	setFieldValue(`${prefix}_city`, result.city || '');
	setFieldValue(`${prefix}_state`, result.state || '');
	setFieldValue(`${prefix}_postcode`, result.postcode || '');

	if (result.address_2) {
		document
			.querySelector(`#${prefix}_address_2_field`)
			?.closest('.woocommerce-billing-fields, .woocommerce-shipping-fields, .shipping_address')
			?.classList.add('is-apartment-open');
	}

	if (typeof window.jQuery !== 'undefined') {
		window.jQuery(document.body).trigger('update_checkout');
		window.jQuery(`#${prefix}_country`).trigger('change');
		window.jQuery(`#${prefix}_state`).trigger('change');
	}
}

function movePhoneIntoBlock(block) {
	const phone = document.getElementById('billing_phone_field');
	if (!phone || !block) {
		return;
	}
	const postcode =
		block.querySelector('#shipping_postcode_field, #billing_postcode_field') ||
		block.querySelector('.form-row-first#billing_postcode_field, .form-row-first#shipping_postcode_field');
	const postcodeRow =
		block.querySelector('#shipping_postcode_field') || block.querySelector('#billing_postcode_field');
	if (postcodeRow && phone.parentElement !== postcodeRow.parentElement) {
		postcodeRow.insertAdjacentElement('afterend', phone);
	} else if (postcode) {
		postcode.insertAdjacentElement('afterend', phone);
	} else {
		block.appendChild(phone);
	}
}

function expandAddress(container, prefix, result) {
	container.classList.add('is-address-expanded');
	const lookup = container.querySelector(`[data-address-lookup="${prefix}"]`);
	if (lookup) {
		lookup.hidden = true;
	}
	if (result) {
		fillAddress(prefix, result);
	}
	ensureApartmentLink(document.getElementById(`${prefix}_address_1_field`));
	const block =
		prefix === 'shipping'
			? container.querySelector('.shipping_address') || container
			: container.querySelector('.woocommerce-billing-fields__field-wrapper') || container;
	movePhoneIntoBlock(block);
	document.dispatchEvent(new CustomEvent('dc:address-expanded', { detail: { prefix } }));
}

function bindLookup(wrap, container, prefix) {
	if (!wrap || wrap.dataset.bound === 'true') {
		return;
	}
	wrap.dataset.bound = 'true';

	const input = wrap.querySelector('[data-address-lookup-input]');
	const results = wrap.querySelector('[data-address-lookup-results]');
	const spinner = wrap.querySelector('[data-address-lookup-spinner]');
	const manual = wrap.querySelector('[data-address-lookup-manual]');
	let activeIndex = -1;
	let items = [];

	const renderResults = (list) => {
		items = list;
		activeIndex = -1;
		if (!results) {
			return;
		}
		if (!list.length) {
			results.hidden = true;
			results.innerHTML = '';
			input?.setAttribute('aria-expanded', 'false');
			return;
		}
		results.innerHTML = list
			.map(
				(item, index) =>
					`<li role="option" class="dc-address-lookup__option" data-index="${index}" id="dc-addr-opt-${prefix}-${index}">${escapeHtml(item.label)}</li>`
			)
			.join('');
		results.hidden = false;
		input?.setAttribute('aria-expanded', 'true');
	};

	const selectIndex = (index) => {
		const item = items[index];
		if (!item) {
			return;
		}
		expandAddress(container, prefix, item);
		if (input) {
			input.value = item.label || '';
		}
		renderResults([]);
	};

	const runSearch = debounce(async () => {
		const query = String(input?.value || '').trim();
		if (query.length < 3) {
			renderResults([]);
			if (spinner) {
				spinner.hidden = true;
			}
			wrap.classList.remove('is-loading');
			return;
		}

		wrap.classList.add('is-loading');
		if (spinner) {
			spinner.hidden = false;
		}

		try {
			const list = await fetchSuggestions(query);
			renderResults(list);
		} catch {
			renderResults([]);
		} finally {
			wrap.classList.remove('is-loading');
			if (spinner) {
				spinner.hidden = true;
			}
		}
	}, 280);

	input?.addEventListener('input', () => {
		wrap.classList.toggle('is-filled', String(input.value || '').trim() !== '');
		runSearch();
	});
	input?.addEventListener('focus', () => {
		wrap.classList.add('is-floating');
	});
	input?.addEventListener('blur', () => {
		wrap.classList.toggle('is-floating', String(input.value || '').trim() !== '');
		window.setTimeout(() => renderResults([]), 150);
	});

	input?.addEventListener('keydown', (event) => {
		if (results?.hidden || !items.length) {
			return;
		}
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			activeIndex = Math.min(items.length - 1, activeIndex + 1);
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			activeIndex = Math.max(0, activeIndex - 1);
		} else if (event.key === 'Enter' && activeIndex >= 0) {
			event.preventDefault();
			selectIndex(activeIndex);
			return;
		} else if (event.key === 'Escape') {
			renderResults([]);
			return;
		} else {
			return;
		}
		results.querySelectorAll('.dc-address-lookup__option').forEach((el, index) => {
			el.classList.toggle('is-active', index === activeIndex);
		});
	});

	results?.addEventListener('mousedown', (event) => {
		const option = event.target.closest('[data-index]');
		if (!option) {
			return;
		}
		event.preventDefault();
		selectIndex(Number(option.getAttribute('data-index')));
	});

	manual?.addEventListener('click', () => {
		expandAddress(container, prefix, null);
		document.getElementById(`${prefix}_address_1`)?.focus();
	});
}

function activeTargets() {
	const method = getDeliveryMethod();
	const billing = document.querySelector('.dc-checkout .woocommerce-billing-fields');
	const shipping = document.querySelector('.dc-checkout .woocommerce-shipping-fields');

	if (method === 'collection') {
		return billing ? [{ container: billing, prefix: 'billing' }] : [];
	}

	if (isBillingPanel()) {
		return billing ? [{ container: billing, prefix: 'billing' }] : [];
	}

	return shipping ? [{ container: shipping, prefix: 'shipping' }] : [];
}

function syncAddressLookup() {
	const checkout = document.querySelector('[data-dc-checkout], .dc-checkout');
	if (!checkout) {
		return;
	}

	// Reset inactive lookups visibility when switching panels/methods.
	checkout.querySelectorAll('[data-address-lookup]').forEach((el) => {
		el.hidden = true;
	});

	activeTargets().forEach(({ container, prefix }) => {
		const wrap = ensureAddressLookup(container, prefix);
		if (!wrap) {
			return;
		}
		bindLookup(wrap, container, prefix);

		const filled = addressLooksFilled(prefix);
		if (filled || container.classList.contains('is-address-expanded')) {
			container.classList.add('is-address-expanded');
			wrap.hidden = true;
			ensureApartmentLink(document.getElementById(`${prefix}_address_1_field`));
			const block =
				prefix === 'shipping'
					? container.querySelector('.shipping_address') || container
					: container.querySelector('.woocommerce-billing-fields__field-wrapper') || container;
			movePhoneIntoBlock(block);
		} else {
			container.classList.remove('is-address-expanded');
			wrap.hidden = false;
		}
	});

	// Apartment links for any visible address_1.
	['billing', 'shipping'].forEach((prefix) => {
		ensureApartmentLink(document.getElementById(`${prefix}_address_1_field`));
	});
}

export function initCheckoutAddressLookup() {
	const checkout = document.querySelector('[data-dc-checkout], .dc-checkout');
	if (!checkout) {
		return;
	}

	syncAddressLookup();

	document.body.addEventListener('updated_checkout', () => {
		window.requestAnimationFrame(() => syncAddressLookup());
	});

	document.addEventListener('dc:delivery-method', () => {
		window.requestAnimationFrame(() => syncAddressLookup());
	});

	document.addEventListener('dc:address-expanded', () => {
		window.requestAnimationFrame(() => syncAddressLookup());
	});

	// Re-sync when Continue/Back toggles billing panel classes.
	const observer = new MutationObserver(() => syncAddressLookup());
	observer.observe(checkout, { attributes: true, attributeFilter: ['class', 'data-delivery-method'] });
}
